import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { del, get, post } from '@api/httpClient';
import { AxiosResponse } from 'axios';

import {
  // type OutputIDAnnotationTableSchema,
  type OutputIDExperimentSchema,
  type AvailableFiltersSchema,
  SavedFilterResults,
  PatientHPOListsSchema,
} from '@/zod_schemas';
import { VariantComment, FiltersState } from '@/pages/common/Results/types';
import { APIError } from '../types';

// // For testing purpose, to mimic delay response time from API
// const delay = (ms: number) =>
//   new Promise((resolve) => {
//     setTimeout(resolve, ms);
//   });

const QUERY_KEYS_OUTPUTID = {
  OUTPUTID: (id: string) => ['outputId', { id }],
};

export const useGetOutputIDExperiment = (id?: string) =>
  useSuspenseQuery<OutputIDExperimentSchema>({
    queryKey: QUERY_KEYS_OUTPUTID.OUTPUTID(id || ''),
    queryFn: async () => {
      if (!id) return {};

      const resp = await get(`/output/${id}/experiment/`);
      return resp.data;
    },
  });

const QUERY_KEYS_ANNOTATION_OUTPUT = {
  ANNOTATION_OUTPUT: (
    finalFilters: FiltersState,
    id: string,
    sources: string[] = [],
    cursor: string | null = null,
  ) => ['outputId', { finalFilters, id, sources, cursor }],
};

export const useGetOutputAnnotationTable = (
  finalFilters: FiltersState = {},
  id?: string,
  sourcesx: string[] = [],
  cursor: string | null = null,
) =>
  useQuery({
    queryKey: QUERY_KEYS_ANNOTATION_OUTPUT.ANNOTATION_OUTPUT(
      finalFilters,
      id || '',
      sourcesx,
      cursor,
    ),
    queryFn: async () => {
      if (!id) return {};

      const queryParams: Record<string, string> = {};
      if (cursor) {
        queryParams.cursor = cursor;
      }

      const queryString = new URLSearchParams(queryParams).toString();
      const url = `/output/${id}/annotation-table/${
        queryString ? `?${queryString}` : ''
      }`;

      const isEmpty = (obj: object) => Object.keys(obj).length === 0;

      let sources: string[] = finalFilters.sources ?? [];
      let { filters } = finalFilters;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      let { selected_only } = finalFilters;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      let { variant_selection } = finalFilters;

      if (isEmpty(finalFilters)) {
        const resp2 = await get(`/output/${id}/filters/`);
        if (resp2.data.request_filters) {
          sources = resp2.data.request_filters.sources;
          filters = resp2.data.request_filters.filters;
          selected_only = resp2.data.request_filters.selected_only;
          variant_selection = 2;
        }
      }

      // await delay(5000); // simulate latency

      const resp = await post(url, {
        sources,
        filters,
        selected_only,
        variant_selection,
      });

      return resp.data;
    },
    enabled: !!id,
  });

export const useGetSelectedVariants = (
  outputId?: string,
  cursor?: string,
  createdBy?: string,
  validationStatus?: string,
) =>
  useQuery({
    queryKey: ['saved_variants', outputId, cursor, createdBy, validationStatus],
    queryFn: async () => {
      if (!outputId) return {};

      const queryParams: Record<string, string> = {};

      // Required param
      queryParams.output_id = outputId;

      // Optional filters
      if (createdBy) queryParams.created_by = createdBy;
      if (validationStatus) queryParams.validation_status = validationStatus;
      if (cursor) queryParams.cursor = cursor;

      const queryString = new URLSearchParams(queryParams).toString();
      const url = `/selected-variants/filter/?${queryString}`;

      const resp = await get(url);
      return resp.data;
    },
    enabled: !!outputId, // only run if outputId is defined
  });

export const useGetAvailableFilters = () =>
  useSuspenseQuery<AvailableFiltersSchema>({
    queryKey: ['available_filters'],
    queryFn: async () => {
      const resp = await get('/output/available_filters/');
      return resp.data;
    },
  });

const QUERY_KEYS_FILTER_OUTPUT = {
  FILTEROUTPUT: (id: string) => ['filterOutput', { id }],
};

export const useGetFilterOutput = (id?: string) =>
  useSuspenseQuery<SavedFilterResults>({
    queryKey: QUERY_KEYS_FILTER_OUTPUT.FILTEROUTPUT(id || ''),
    queryFn: async () => {
      if (!id) return {};
      const resp = await get(`/output/${id}/filters/`);
      return resp.data;
    },
  });

const QUERY_KEYS_FILTER_PATIENT_HPO = {
  PATIENTHPO: (id: string) => ['patientHPO', { id }],
};

// export const useGetPatientHPO = (id?: string) =>
//   useQuery({
//     queryKey: QUERY_KEYS_FILTER_PATIENT_HPO.PATIENTHPO(id || ''),
//     queryFn: async () => {
//       if (!id) return {};
//       const resp = await get(`/output/${id}/patient_hpo_ids/`);
//       return resp.data;
//     },
//     enabled: !!id,
//   });

export const useGetPatientHPO = (id?: string) =>
  useSuspenseQuery<PatientHPOListsSchema>({
    queryKey: QUERY_KEYS_FILTER_PATIENT_HPO.PATIENTHPO(id || ''),
    queryFn: async () => {
      if (!id) return {};
      const resp = await get(`/output/${id}/patient_hpo_ids/`);
      return resp.data;
    },
  });

interface CreateSamplePayload {
  outputId: string;
  variantIds: string[];
}
export const useAddVariants = () =>
  useMutation<AxiosResponse<string[]>, APIError, CreateSamplePayload>({
    mutationFn: async ({ outputId, variantIds }) =>
      post<string[]>(`/output/${outputId}/add_selected_variants/`, {
        result_vcf_ids: variantIds,
      }),
  });

export const useRemoveVariants = () =>
  useMutation<AxiosResponse<string[]>, APIError, CreateSamplePayload>({
    mutationFn: async ({ outputId, variantIds }) =>
      post<string[]>(`/output/${outputId}/remove_selected_variants/`, {
        result_vcf_ids: variantIds,
      }),
  });

export const useFirstValidateVariant = () =>
  useMutation({
    mutationFn: async ({ id, userType }: { id: string; userType: string }) => {
      const url =
        userType === 'first-validate'
          ? `/selected-variants/${id}/first-validate/`
          : `/selected-variants/${id}/second-validate/`;
      const res = await post(url, {});
      return res.data;
    },
  });

export const useRejectVariant = () =>
  useMutation({
    mutationFn: async ({ id, comment }: { id: string; comment?: string }) => {
      const res = await post(`/selected-variants/${id}/reject/`, {
        round_number: 2, // Default to 2(reject for 2nd validation) -> 1st does not need to reject
        comment: comment ?? '',
      });
      return res.data;
    },
  });

// api/result/result.ts
type CommentType =
  | 'general'
  | 'first_validation'
  | 'second_validation'
  | 'rejection';

interface CommentVariantInput {
  id: string;
  comment: string;
  comment_type?: CommentType; // default later
}

export const useCommentVariant = () =>
  useMutation({
    mutationFn: async ({
      id,
      comment,
      comment_type = 'general',
    }: CommentVariantInput) => {
      const res = await post(`/selected-variants/${id}/add-comment/`, {
        comment_type,
        comment,
      });
      return res.data as VariantComment;
    },
  });

const QUERY_KEYS_VARIANT_COMMENTS = {
  ID: (id: string) => ['variantId', { id }],
};
// export const useGetVariantComments = (id?: string) =>
//   useSuspenseQuery<Comment[]>({
//     queryKey: QUERY_KEYS_VARIANT_COMMENTS.ID(id || ''),
//     queryFn: async () => {
//       if (!id) return {};

//       const resp = await get(`/selected-variants/${id}/comments/`);
//       return resp.data;
//     },
//   });

export const useGetVariantComments = (id?: string) =>
  useQuery<VariantComment[]>({
    queryKey: QUERY_KEYS_VARIANT_COMMENTS.ID(id ?? ''),
    queryFn: async () => {
      if (!id) return []; // always return an array
      const resp = await get(`/selected-variants/${id}/comments/`);
      return Array.isArray(resp.data) ? resp.data : [];
    },
    enabled: !!id,
  });

export const useDeleteVariantComment = () =>
  useMutation<{ detail?: string }, APIError, { id: string; commentId: string }>(
    {
      mutationFn: async ({ id, commentId }) => {
        const resp = await del(
          `/selected-variants/${id}/comments/${commentId}/`,
        );
        return resp.data;
      },
    },
  );
